import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockEditor from '../components/blocks/BlockEditor';
import MediaLibraryDialog from '../components/blocks/MediaLibraryDialog';
import { db } from '../firebase';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { renderBlocks } from '../components/blocks/renderBlocks';
import type { ContentBlock } from '../components/blocks/BlockEditor';

type PolicyType = 'terms' | 'privacy' | 'tokushoho';

interface PolicyDoc {
  id?: string;
  title?: string;
  blocks?: ContentBlock[];
  updatedAt?: any;
  createdAt?: any;
}

export default function PolicyEditPage() {
  const navigate = useNavigate();
  const { type } = useParams<{ type: PolicyType }>();
  const policyType: PolicyType = (type === 'privacy' ? 'privacy' : type === 'tokushoho' ? 'tokushoho' : 'terms');
  const [policy, setPolicy] = useState<PolicyDoc>({ title: '', blocks: [] });
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const pendingResolverRef = useRef<((val: { url: string; alt?: string } | null) => void) | null>(null);

  const title = useMemo(() => (
    policyType === 'terms' ? '利用規約' : policyType === 'privacy' ? 'プライバシーポリシー' : '特定商取引法に基づく表記'
  ), [policyType]);

  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'policies', policyType));
      if (snap.exists()) {
        const data = snap.data() as PolicyDoc;
        setPolicy({ id: snap.id, title: data.title || title, blocks: data.blocks || [] });
      } else {
        setPolicy({ title, blocks: [] });
      }
    })();
  }, [policyType, title]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const ref = doc(db, 'policies', policyType);
      await setDoc(ref, {
        title: policy.title || title,
        blocks: policy.blocks || [],
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      }, { merge: true });
      navigate('/content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/content')} sx={{ mr: 2 }}>戻る</Button>
        <Typography variant="h4">{title} 編集</Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>内容</Typography>
        <BlockEditor
          value={policy.blocks || []}
          onChange={(next) => setPolicy((p) => ({ ...p, blocks: next }))}
          onPickImage={() => {
            setMediaOpen(true);
            return new Promise((resolve) => {
              pendingResolverRef.current = resolve;
            });
          }}
        />

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 3 }}>プレビュー</Typography>
        {renderBlocks(policy.blocks || [])}

        <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
          <Button variant="contained" disabled={saving} onClick={handleSave}>保存</Button>
        </Box>
      </Card>

      <MediaLibraryDialog
        open={mediaOpen}
        onClose={() => {
          setMediaOpen(false);
          if (pendingResolverRef.current) {
            pendingResolverRef.current(null);
            pendingResolverRef.current = null;
          }
        }}
        onSelect={(item) => {
          setMediaOpen(false);
          if (pendingResolverRef.current) {
            pendingResolverRef.current({ url: item.url });
            pendingResolverRef.current = null;
          }
        }}
      />
    </Box>
  );
}


